import { ReactNode, useLayoutEffect, useMemo } from 'react';
import { v4 } from 'uuid';
import { CellulaTheme } from '../../core/theme';
import { getCellulaLanguage } from '../../utils/get_cellula_language';
import { CellulaTexts } from '../../utils/hooks/use_text';
import { AppNotificationsProvider } from '../app_notifications';
import { CellulaI18nProvider, CellulaLanguage } from '../i18n_provider';
import { LayoutProvider } from '../layout';
import { TooltipProvider } from '../tooltip';
import { CellulaProviderContext } from './cellula_provider.context';
import { ThemeBuilder } from './theme_builder';

interface TokenOverrides {
	[key: string]: string;
}

interface InternalProviderProps {
	id: string;
	theme: CellulaTheme;
	tokenOverrides?: TokenOverrides;
	portalElement?: HTMLElement;
	children: ReactNode;
}

function RootProvider({ id, theme, tokenOverrides, children }: InternalProviderProps): JSX.Element {
	useLayoutEffect(
		function whenThemeOrTokenOverridesChanges() {
			ThemeBuilder.insertTokens(id, theme, true, tokenOverrides);
		},
		[id, theme, tokenOverrides],
	);

	return (
		<CellulaProviderContext.Provider value={{ cellulaId: id, portalElement: document.body }}>
			{children}
		</CellulaProviderContext.Provider>
	);
}

function ScopedProvider({
	id,
	theme,
	tokenOverrides,
	portalElement: controlledPortalElement,
	children,
}: InternalProviderProps): JSX.Element {
	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	const portalElement = useMemo(() => controlledPortalElement ?? document.createElement('div'), []);

	useLayoutEffect(
		function whenThemeOrTokenOverridesChanges() {
			ThemeBuilder.insertTokens(id, theme, false, tokenOverrides);
		},
		[id, theme, tokenOverrides],
	);

	useLayoutEffect(
		function whenComponentMounts() {
			portalElement.setAttribute('data-cellula-id', id);

			if (controlledPortalElement) {
				return;
			}

			document.body.appendChild(portalElement);

			return () => {
				document.body.removeChild(portalElement);
			};
		},
		[controlledPortalElement, portalElement, id],
	);

	return (
		<CellulaProviderContext.Provider value={{ cellulaId: id, portalElement }}>
			<div
				style={{
					display: 'contents',
				}}
				data-cellula-id={id}
			>
				{children}
			</div>
		</CellulaProviderContext.Provider>
	);
}

interface CellulaProviderProps {
	/**
	 * Provide a Cellula theme.
	 */
	theme: CellulaTheme;

	/**
	 * Use to override and add own tokens beside core tokens.
	 */
	tokenOverrides?: TokenOverrides;

	/**
	 * Use if you don't want to scope Cellula to it's own scope. THis will run
	 * Cellula in a global scope. This means that CSS variables will be set in
	 * `:root`.
	 */
	providedInRoot?: boolean;

	lang?: string;

	/**
	 * Override specific static texts
	 */
	textOverrides?: Partial<CellulaTexts>;
	portalElement?: HTMLElement;
	children: ReactNode;
}

function CellulaProvider({
	providedInRoot = false,
	lang = 'en',
	textOverrides,
	children,
	...rest
}: CellulaProviderProps): JSX.Element {
	const id = useMemo(() => v4(), []);
	const cellulaLang = getCellulaLanguage(lang);

	if (providedInRoot) {
		return (
			<RootProvider id={id} {...rest}>
				<Providers lang={cellulaLang} textOverrides={textOverrides}>
					{children}
				</Providers>
			</RootProvider>
		);
	}

	return (
		<ScopedProvider id={id} {...rest}>
			<Providers lang={cellulaLang} textOverrides={textOverrides}>
				{children}
			</Providers>
		</ScopedProvider>
	);
}

function Providers({
	lang,
	textOverrides,
	children,
}: {
	lang: CellulaLanguage;
	textOverrides?: TokenOverrides;
	children: ReactNode;
}): JSX.Element {
	return (
		<CellulaI18nProvider lang={lang} textOverrides={textOverrides}>
			<AppNotificationsProvider>
				<LayoutProvider>
					<TooltipProvider>{children}</TooltipProvider>
				</LayoutProvider>
			</AppNotificationsProvider>
		</CellulaI18nProvider>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

export type { TokenOverrides };
/**
 * The staring point of Cellula. This component is required to make cellula
 * work. It will provide all components with theme related thing like colors,
 * margins, etc. It will also scope everything to it's own scope. Which means
 * you can have different scopes with different themes through the same
 * application.
 */
export { CellulaProvider };
